import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Avatar, Chip, Button,
  CircularProgress, Alert, Divider,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedIcon from '@mui/icons-material/Verified';
import LockIcon from '@mui/icons-material/Lock';
import { validarCarnet } from '../services/perfilApi';

/**
 * Página pública de validación de carnet.
 * URL: /v/:codigo
 *
 * Patrón híbrido: validación pública mínima + botón a portal.
 *  - Banner ✓/✗ con estado del carnet
 *  - Datos mínimos del titular (foto, nombre, cargo)
 *  - Documento enmascarado
 *  - Vigencia y fecha de expedición
 *  - Botón "Ir al portal" según cooperativa
 *  - Botón "Iniciar sesión para ver perfil completo"
 */
export default function ValidacionCarnet() {
  const { codigo } = useParams();
  const [estado, setEstado] = useState({ loading: true, data: null, error: '' });

  useEffect(() => {
    (async () => {
      try {
        const data = await validarCarnet(codigo);
        setEstado({ loading: false, data, error: '' });
      } catch (err) {
        setEstado({
          loading: false,
          data: null,
          error: err?.data?.mensaje || 'No se pudo validar el carnet',
        });
      }
    })();
  }, [codigo]);

  if (estado.loading) {
    return (
      <Box sx={{ minHeight: '100vh', pt: 14, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#2e7d32' }} />
      </Box>
    );
  }

  if (estado.error || !estado.data) {
    return (
      <Box sx={{ minHeight: '100vh', background: '#f5f7f4', pt: { xs: 11, md: 13 }, pb: 8 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, textAlign: 'center' }}>
            <ErrorOutlineIcon sx={{ fontSize: 80, color: '#b71c1c', mb: 2 }} />
            <Typography variant="h5" sx={{ color: '#1a1a1a', fontWeight: 700, mb: 1 }}>
              Carnet no encontrado
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
              {estado.error || 'El código que escaneó no corresponde a ningún carnet válido.'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
              Código consultado: <strong style={{ fontFamily: 'monospace' }}>{codigo}</strong>
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  const d = estado.data;
  const esCapellanes = d.cooperativa?.siglas === 'CAPELLANES';
  const portal = esCapellanes
    ? { url: 'https://solidarityallianceofchaplains.org/', label: 'Portal de la Alianza' }
    : { url: 'https://fenagro-oficial.org/afiliacion', label: 'Portal FENAGRO APL' };

  const palette = esCapellanes
    ? { main: '#1e3a5f', accent: '#c8a84b' }
    : { main: '#1a4d2e', accent: '#c8a84b' };

  const initials = ((d.titular?.nombre?.[0] || '') + (d.titular?.apellido?.[0] || '')).toUpperCase();

  return (
    <Box sx={{ minHeight: '100vh', background: '#f5f7f4', pb: 8 }}>
      {/* Mini header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${palette.main}, ${palette.accent})`,
        py: 2, px: 3, mb: 3, color: '#fff',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
      }}>
        <Container maxWidth="sm" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1, background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: '1.2rem',
          }}>
            {esCapellanes ? '✝' : 'F'}
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, lineHeight: 1.2 }}>
              {esCapellanes ? 'Alianza Solidaria de Capellanes' : 'FENAGRO APL'}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', opacity: 0.85, letterSpacing: '0.04em' }}>
              Verificación pública de carnet
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="sm">

        {/* Banner de estado */}
        <Paper sx={{
          mb: 2, p: 2, borderRadius: 3,
          background: d.valido
            ? 'linear-gradient(135deg, #2e7d32, #43a047)'
            : 'linear-gradient(135deg, #b71c1c, #d32f2f)',
          color: '#fff', textAlign: 'center',
          boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            {d.valido ? <VerifiedIcon sx={{ fontSize: 36 }}/> : <ErrorOutlineIcon sx={{ fontSize: 36 }}/>}
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {d.valido ? 'Carnet vigente' : d.mensaje}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
                Estado: <strong>{d.estado}</strong>
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Tarjeta del titular */}
        <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 3 }}>
            <Avatar
              src={d.titular?.fotoUrl || undefined}
              sx={{
                width: 96, height: 96,
                background: `linear-gradient(135deg, ${palette.main}, ${palette.accent})`,
                color: '#fff', fontWeight: 700, fontSize: '2rem',
                border: `3px solid ${palette.accent}`,
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h5" sx={{ color: '#1a1a1a', fontWeight: 700, lineHeight: 1.25, mb: 0.5 }}>
                {d.titular?.nombre} {d.titular?.apellido}
              </Typography>
              <Chip
                label={d.cooperativa?.nombre || ''}
                size="small"
                sx={{
                  background: `${palette.main}15`,
                  color: palette.main, fontWeight: 600,
                  border: `0.5px solid ${palette.main}30`,
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }}/>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <DataItem label="Documento" value={`${d.titular?.tipoDocumento || ''} ${d.titular?.documento || '—'}`}/>
            <DataItem label="Código" value={d.carnet?.codigo} mono/>
            {d.carnet?.numeroRegistro && (
              <DataItem label="N° Registro" value={d.carnet.numeroRegistro} mono accent={palette.accent}/>
            )}
            <DataItem label="Expedición" value={fmtFecha(d.carnet?.fechaExpedicion)}/>
            <DataItem label="Vigencia" value={fmtFecha(d.carnet?.fechaVigencia)}/>
          </Box>
        </Paper>

        {/* Audit info */}
        <Alert severity="info" icon={<LockIcon/>} sx={{ mb: 2, fontSize: '0.84rem' }}>
          Esta es una verificación pública. Para acceder a la información completa
          del afiliado se requiere iniciar sesión en el portal.
          <br/>
          <span style={{ fontSize: '0.78rem', opacity: 0.85 }}>
            Verificado el {new Date(d.verificadoEn).toLocaleString('es-CO')}
          </span>
        </Alert>

        {/* Botones */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="contained" size="large"
            startIcon={<OpenInNewIcon/>}
            onClick={() => window.open(portal.url, '_blank', 'noopener,noreferrer')}
            sx={{
              background: `linear-gradient(135deg, ${palette.main}, ${palette.accent})`,
              fontWeight: 700, px: 3, py: 1.3,
              '&:hover': { filter: 'brightness(1.08)' },
            }}
          >
            {portal.label}
          </Button>

          <Button
            variant="outlined" size="large"
            onClick={() => window.location.href = '/login'}
            sx={{ color: palette.main, borderColor: palette.main, fontWeight: 600 }}
          >
            Iniciar sesión
          </Button>
        </Box>

      </Container>
    </Box>
  );
}

function DataItem({ label, value, mono, accent }) {
  return (
    <Box>
      <Typography sx={{ fontSize: '0.7rem', color: '#888', letterSpacing: '0.06em', mb: 0.3 }}>
        {label.toUpperCase()}
      </Typography>
      <Typography sx={{
        fontSize: '0.9rem', color: accent || '#1a1a1a', fontWeight: accent ? 700 : 500,
        fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-all',
      }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

function fmtFecha(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
}
